import { Check, Copy, ExternalLink } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import type { ClaudeCodeAuth } from "@ide/shared/desktop";
import { Button } from "@/components/ui/button";
import { Input, Spinner } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import { hasCodeEngine, refreshEngine } from "@/workspace/code-engine";
import { useLocalCopy } from "@/workspace/local-copy";
import { newTerminal, useTerminalStore } from "@/workspace/terminal-panel";
import { ConnectAnthropic } from "./claude-connect";

const THIRD_PARTY_DOCS = "https://docs.claude.com/en/docs/claude-code/third-party-integrations";

/**
 * Abre o terminal na sua máquina (na pasta do projeto) e roda o Claude Code: lá ele entra com a
 * assinatura do Claude (Pro, Max, Team, Enterprise) pela própria Anthropic. É o "Prefere o
 * terminal? Rode claude" do Claude Code no VS Code.
 */
export async function runClaudeInTerminal() {
  if (!desktop) {
    toast("O Claude Code no terminal roda no Kompil Desktop.");
    return;
  }
  newTerminal("local");
  for (let i = 0; i < 150; i++) {
    const { connection, status } = useTerminalStore.getState();
    if (connection?.kind === "local" && status === "ready") {
      await connection.run("claude");
      toast("Claude Code no terminal", {
        description: "Sem o Claude Code instalado? Rode: npm install -g @anthropic-ai/claude-code",
      });
      return;
    }
    await new Promise((r) => setTimeout(r, 200));
  }
  toast.error(
    useLocalCopy.getState().info?.enabled
      ? "O terminal local não ficou pronto. Tente de novo."
      : "Ligue a cópia local do projeto para usar o terminal na sua máquina.",
  );
}

const CLAUDE_CODE = "anthropic.claude-code";

const PLAN: Record<string, string> = { pro: "Pro", max: "Max", team: "Team", enterprise: "Enterprise" };
export const planOf = (subscription?: string) => (subscription ? (PLAN[subscription] ?? subscription) : null);

type Phase = "idle" | "checking" | "installing" | "waiting" | "error";

/**
 * Conta do Claude Code oficial (o mesmo login do plugin do VS Code): quem entra é o Claude Code, que
 * abre no navegador a página da Anthropic pedindo a sua permissão. O estado fica aqui para o painel
 * mostrar cada passo (abrindo, esperando a autorização, colar o código) e o resultado (e-mail, plano).
 */
export const useClaudeCode = create<{
  auth: ClaudeCodeAuth | null;
  phase: Phase;
  method: "claudeai" | "console";
  /** Link da autorização (se o navegador não abriu, ou para pegar o código). */
  url: string | null;
  error: string | null;
}>(() => ({ auth: null, phase: "idle", method: "claudeai", url: null, error: null }));

/** Relê a conta do Claude Code (e-mail, plano, versão). */
export async function refreshClaudeCode(): Promise<ClaudeCodeAuth | null> {
  const api = desktop?.claudeCode;
  if (!api) return null;
  const busy = useClaudeCode.getState().phase;
  if (busy === "idle" || busy === "error") useClaudeCode.setState({ phase: "checking" });
  try {
    const auth = await api.status();
    useClaudeCode.setState((s) => ({ auth, phase: s.phase === "checking" ? "idle" : s.phase }));
    return auth;
  } catch {
    useClaudeCode.setState((s) => ({ phase: s.phase === "checking" ? "idle" : s.phase }));
    return null;
  }
}

/** Sem Claude Code na máquina: instala a extensão oficial no motor VS Code do Kompil. */
async function ensureClaudeCodePlugin(): Promise<boolean> {
  const api = desktop?.codeEngine;
  if (!api) return false;
  if ((await api.extensions()).some((e) => e.id === CLAUDE_CODE)) return true;
  useClaudeCode.setState({ phase: "installing" });
  const status = await api.status();
  if (!["ready", "running", "starting", "importing"].includes(status.state)) await api.install();
  const res = await api.installExtension(CLAUDE_CODE);
  if (res.ok) await refreshEngine();
  return res.ok;
}

/**
 * Entrar no Claude Code: com a assinatura do Claude.ai (Pro, Max, Team, Enterprise) ou com a conta
 * do Console. Abre a página da Anthropic no navegador; autorizado, o Claude Code do motor, do
 * terminal e do VS Code passam a usar a conta. A Anthropic só libera o login da assinatura para os
 * apps dela, por isso quem entra é o Claude Code, não o chat do Kompil.
 */
export async function signInWithClaudeCode(method: "claudeai" | "console" = "claudeai") {
  const login = desktop?.claudeCode;
  if (!login) {
    await runClaudeInTerminal();
    return;
  }
  useClaudeCode.setState({ method, url: null, error: null, phase: "checking" });
  let stopUrl: (() => void) | null = null;
  try {
    let auth = await login.status();
    if (!auth.available) {
      if (!(await ensureClaudeCodePlugin())) {
        useClaudeCode.setState({
          auth,
          phase: "error",
          error:
            "Não deu para instalar o Claude Code (extensão oficial da Anthropic). Confira a internet e tente de novo, ou instale pelo terminal: npm install -g @anthropic-ai/claude-code",
        });
        return;
      }
      auth = await login.status();
    }
    useClaudeCode.setState({ auth, phase: "waiting" });
    stopUrl = login.onLoginUrl((url) => useClaudeCode.setState({ url }));
    const res = await login.login(method);
    if (res.ok) {
      useClaudeCode.setState({ auth: res.auth ?? auth, phase: "idle", url: null, error: null });
      const plan = planOf(res.auth?.subscription);
      toast.success(plan ? `Claude ${plan} conectado ao Claude Code` : "Conta do Claude conectada", {
        description: res.auth?.email ? `Entrou como ${res.auth.email}.` : undefined,
      });
    } else {
      useClaudeCode.setState({
        ...(res.auth ? { auth: res.auth } : {}),
        phase: "error",
        url: null,
        error: res.error ?? "A autorização não foi concluída. Tente de novo.",
      });
    }
  } catch (err) {
    useClaudeCode.setState({
      phase: "error",
      url: null,
      error: err instanceof Error ? err.message : String(err),
    });
  } finally {
    stopUrl?.();
  }
}

export async function submitClaudeCode(code: string): Promise<boolean> {
  const ok = (await desktop?.claudeCode?.submitCode?.(code)) ?? false;
  if (!ok)
    toast.error("Não deu para enviar o código", { description: "Entre de novo e copie o código novo." });
  return ok;
}

export async function signOutOfClaudeCode() {
  const api = desktop?.claudeCode;
  if (!api?.logout) return;
  const auth = await api.logout();
  useClaudeCode.setState({ auth, phase: "idle", error: null, url: null });
  toast("Você saiu da conta do Claude no Claude Code");
}

/** Passo a passo enquanto o login acontece no navegador (com o "colar o código"). */
function ClaudeCodeProgress() {
  const { phase, url, method } = useClaudeCode();
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const steps = [
    { label: "Abrir a página da Anthropic no navegador", done: phase === "waiting" },
    {
      label:
        method === "console"
          ? "Entrar na sua conta do Console e permitir o Claude Code"
          : "Entrar na sua conta do Claude (ou criar uma) e permitir o Claude Code",
      done: false,
    },
    { label: "Voltar para o Kompil: a conexão termina sozinha", done: false },
  ];
  return (
    <div className="mx-auto w-full max-w-[460px] px-5 pb-6 pt-6 text-[13px] leading-relaxed text-fg-muted">
      <div className="flex items-center gap-2 text-[13.5px] font-medium text-fg">
        <Spinner className="h-3.5 w-3.5" />
        {phase === "installing"
          ? "Instalando o Claude Code (extensão oficial da Anthropic)…"
          : phase === "checking"
            ? "Procurando o Claude Code…"
            : "Esperando a sua autorização no navegador…"}
      </div>
      {phase === "waiting" ? (
        <>
          <ol className="mt-4 space-y-2">
            {steps.map((s, i) => (
              <li key={s.label} className="flex items-start gap-2.5">
                <span
                  className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border text-[10.5px] ${
                    s.done ? "border-fg bg-fg text-fg-inverse" : "border-border-strong text-fg-subtle"
                  }`}
                >
                  {s.done ? <Check className="h-3 w-3" strokeWidth={3} /> : i + 1}
                </span>
                <span className={s.done ? "text-fg" : ""}>{s.label}</span>
              </li>
            ))}
          </ol>
          <div className="mt-5 rounded-md border border-border bg-bg-subtle p-3">
            <p className="text-[12.5px] text-fg-muted">
              O navegador não abriu, ou a página mostrou um código? Abra a página por aqui e cole o código
              abaixo.
            </p>
            <div className="mt-2.5 flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={!url}
                onClick={() => url && (desktop?.openExternal(url) ?? window.open(url, "_blank"))}
              >
                <ExternalLink className="h-3.5 w-3.5" /> Abrir a página
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={!url}
                onClick={() => {
                  if (!url) return;
                  void navigator.clipboard.writeText(url).then(() => toast("Link copiado"));
                }}
              >
                <Copy className="h-3.5 w-3.5" /> Copiar o link
              </Button>
            </div>
            <form
              className="mt-2.5 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!code.trim()) return;
                setSending(true);
                void submitClaudeCode(code).finally(() => {
                  setSending(false);
                  setCode("");
                });
              }}
            >
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.trim())}
                placeholder="Cole aqui o código da página"
                aria-label="Código de autorização da Anthropic"
                className="h-8 font-mono text-[12px]"
                autoComplete="off"
                spellCheck={false}
              />
              <Button size="sm" type="submit" variant="primary" disabled={!code.trim()} loading={sending}>
                Enviar
              </Button>
            </form>
          </div>
        </>
      ) : null}
      <button
        type="button"
        onClick={() => {
          desktop?.claudeCode?.cancel();
          useClaudeCode.setState({ phase: "idle", url: null });
        }}
        className="mt-4 text-[12px] text-fg-subtle hover:text-fg"
      >
        Cancelar
      </button>
    </div>
  );
}

/** Conta conectada (fora de um projeto): quem é e o convite para abrir um projeto. */
function ClaudeCodeConnected({ auth, onApiKey }: { auth: ClaudeCodeAuth; onApiKey: () => void }) {
  const plan = planOf(auth.subscription);
  return (
    <div className="mx-auto w-full max-w-[460px] px-5 pb-6 pt-6 text-[13px] leading-relaxed text-fg-muted">
      <div className="flex items-center gap-2 text-[14px] font-medium text-fg">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-success/15 text-success">
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
        Conectado ao Claude Code
      </div>
      <p className="mt-2">
        {auth.email}
        {auth.method === "claude.ai" ? ` · Claude ${plan ?? ""}`.trimEnd() : " · Console"}
      </p>
      <p className="mt-3 text-[12.5px]">Abra um projeto para conversar com o Claude sobre o código dele.</p>
      <div className="mt-4 flex items-center gap-3 text-[12px]">
        <button
          type="button"
          onClick={onApiKey}
          className="text-fg-muted underline-offset-4 hover:text-fg hover:underline"
        >
          Usar uma chave da API
        </button>
        <span className="text-fg-subtle">·</span>
        <button
          type="button"
          onClick={() => void signOutOfClaudeCode()}
          className="text-fg-muted underline-offset-4 hover:text-fg hover:underline"
        >
          Sair da conta
        </button>
      </div>
    </div>
  );
}

// ───────────── Cena em pixel art (a mesma do Claude Code no VS Code) ─────────────

const CLAWD = "#d97757";
const PX = 4;

/** Desenha um bitmap: cada letra é uma cor da paleta, "." é vazio. */
function Pixels({
  rows,
  x,
  y,
  size = PX,
  palette,
}: {
  rows: readonly string[];
  x: number;
  y: number;
  size?: number;
  palette: Record<string, string>;
}) {
  const rects: ReactElement[] = [];
  rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      const fill = palette[row[c]!];
      if (fill)
        rects.push(
          <rect key={`${r}-${c}`} x={x + c * size} y={y + r * size} width={size} height={size} fill={fill} />,
        );
    }
  });
  return <g shapeRendering="crispEdges">{rects}</g>;
}

/** Nuvem com o meio cheio e as bordas pontilhadas (como no VS Code). */
const CLOUD_BIG = [
  "..........#+#+#+#+#.............",
  "........#+#########+#...........",
  "......+#############+#+#+.......",
  "....#+##################+#+#....",
  "..+#########################+#..",
  "#+############################+#",
  "+##############################+",
] as const;

const CLOUD_SMALL = [
  ".........#+#+#+#..........",
  ".......#+#######+#........",
  "...+#+############+#+.....",
  ".+#####################+#.",
  "+#######################+#",
  "#########################+",
] as const;

/** O Clawd: corpo laranja, olhos, bracinhos e quatro perninhas. */
const CLAWD_ROWS = [
  "..oooooooooo..",
  "..okoooooooko.",
  "..okoooooooko.",
  "oooooooooooooo",
  "oooooooooooooo",
  "..oooooooooo..",
  "..oooooooooo..",
  "..o.o....o.o..",
  "..o.o....o.o..",
] as const;

/** Lua crescente pixelada, com a borda de dentro pontilhada. */
function moonRows(size = 20): string[] {
  const rows: string[] = [];
  const r = size / 2;
  for (let y = 0; y < size; y++) {
    let row = "";
    for (let x = 0; x < size; x++) {
      const dx = x + 0.5 - r;
      const dy = y + 0.5 - r;
      const outer = Math.hypot(dx, dy);
      const inner = Math.hypot(dx - r * 0.55, dy - r * 0.1);
      if (outer > r || inner < r * 0.78) row += ".";
      else if (inner < r * 0.98) row += (x + y) % 2 === 0 ? "+" : ".";
      else if (outer > r - 1.2) row += "+";
      else row += "#";
    }
    rows.push(row);
  }
  return rows;
}

const MOON = moonRows();

const STARS: ReadonlyArray<readonly [number, number, number]> = [
  [118, 92, 0],
  [228, 26, 1.2],
  [292, 48, 2.1],
  [196, 72, 0.6],
  [430, 186, 1.6],
  [394, 82, 2.6],
  [252, 116, 0.3],
  [384, 182, 1.9],
];

export function ClaudeCodeScene({ className }: { className?: string }) {
  const cloud = { "#": "var(--claude-scene-cloud)", "+": "var(--claude-scene-cloud-dim)" };
  const moon = { "#": "var(--claude-scene-moon)", "+": "var(--claude-scene-moon-dim)" };
  return (
    <svg
      viewBox="0 0 480 214"
      className={className}
      role="img"
      aria-label="Clawd, o mascote do Claude Code, sob as nuvens e a lua"
    >
      <Pixels rows={CLOUD_BIG} x={102} y={36} palette={cloud} />
      <Pixels rows={CLOUD_SMALL} x={282} y={112} palette={cloud} />
      <Pixels rows={MOON} x={350} y={30} size={4} palette={moon} />
      {STARS.map(([x, y, delay]) => (
        <text
          key={`${x}-${y}`}
          x={x}
          y={y}
          className="claude-scene-star"
          style={{ animationDelay: `${delay}s` }}
          fontFamily="var(--font-mono)"
          fontSize="15"
          fill="var(--claude-scene-star)"
        >
          *
        </text>
      ))}
      <g className="claude-scene-clawd">
        <Pixels rows={CLAWD_ROWS} x={120} y={164} size={5} palette={{ o: CLAWD, k: "#000000" }} />
      </g>
      <line
        x1="18"
        x2="462"
        y1="209.5"
        y2="209.5"
        stroke="var(--claude-scene-ground)"
        strokeWidth="1"
        strokeDasharray="2 2"
      />
    </svg>
  );
}

/**
 * Como entrar — a mesma tela do Claude Code no VS Code: a assinatura do Claude.ai (pela extensão
 * oficial ou pelo `claude` no terminal: a Anthropic não libera o login da assinatura para outros
 * apps), o Anthropic Console (a chave da API, cobrada pelo uso) ou Bedrock / Foundry / Vertex.
 */
export function ClaudeLogin({ onDone }: { onDone: () => void }) {
  const [console, setConsole] = useState(false);
  const { auth, phase, error } = useClaudeCode();
  useEffect(() => {
    if (hasCodeEngine()) void refreshEngine();
    void refreshClaudeCode();
  }, []);
  const busy = phase === "checking" || phase === "installing" || phase === "waiting";
  if (busy && (phase !== "checking" || !auth)) {
    return (
      <div className="claude-login flex h-full flex-col overflow-y-auto">
        <ClaudeCodeScene className="mx-auto mt-2 w-full max-w-[480px] shrink-0" />
        <ClaudeCodeProgress />
      </div>
    );
  }
  if (auth?.loggedIn && !console) {
    return (
      <div className="claude-login flex h-full flex-col overflow-y-auto">
        <ClaudeCodeScene className="mx-auto mt-2 w-full max-w-[480px] shrink-0" />
        <ClaudeCodeConnected auth={auth} onApiKey={() => setConsole(true)} />
      </div>
    );
  }
  if (console) {
    return (
      <div className="flex h-full flex-col overflow-y-auto px-5 py-6">
        <ConnectAnthropic onDone={onDone} />
        <button
          type="button"
          onClick={() => setConsole(false)}
          className="mt-3 self-center text-[12px] text-fg-subtle hover:text-fg"
        >
          Voltar
        </button>
      </div>
    );
  }
  const option =
    "flex h-[30px] w-full items-center justify-center gap-1.5 rounded-[3px] px-3 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-50";
  const caption = "mt-1.5 text-[12.5px] leading-snug text-fg-subtle";
  return (
    <div className="claude-login flex h-full flex-col overflow-y-auto">
      <ClaudeCodeScene className="mx-auto mt-2 w-full max-w-[480px] shrink-0" />
      <div className="mx-auto w-full max-w-[460px] px-5 pb-6 pt-7 text-[13px] leading-relaxed text-fg-muted">
        <p>
          Claude Code can be used with your Claude subscription or billed based on API usage through your
          Console account.
        </p>
        {phase === "error" && error ? (
          <div
            role="alert"
            className="mt-4 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12.5px] text-fg"
          >
            <p className="font-medium">Não conectou ao Claude</p>
            <p className="mt-0.5 text-fg-muted">{error}</p>
          </div>
        ) : null}
        <p className="mt-4">How do you want to log in?</p>
        <div className="mt-4">
          <button
            type="button"
            onClick={() => void signInWithClaudeCode("claudeai")}
            disabled={!desktop}
            className={`${option} bg-[#d97757] font-medium text-white hover:bg-[#c96646]`}
          >
            Claude.ai Subscription
          </button>
          <p className={caption}>Use your Claude Pro, Team, or Enterprise subscription</p>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setConsole(true)}
            disabled={!desktop}
            className={`${option} border border-border bg-bg-subtle text-fg hover:bg-bg-muted`}
          >
            Anthropic Console
          </button>
          <p className={caption}>Pay for API usage through your Console account</p>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={() => desktop?.openExternal(THIRD_PARTY_DOCS) ?? window.open(THIRD_PARTY_DOCS, "_blank")}
            className={`${option} border border-border bg-bg-subtle text-fg hover:bg-bg-muted`}
          >
            Bedrock, Foundry, or Vertex <ExternalLink className="h-3.5 w-3.5 opacity-70" />
          </button>
          <p className={caption}>Instructions on how to use API keys or third-party providers.</p>
        </div>
        {!desktop ? (
          <p className="mt-5 text-center text-[12px] text-fg-subtle">
            Entrar com a sua conta é no Kompil Desktop.
          </p>
        ) : null}
      </div>
    </div>
  );
}
